
import React, { useState } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Badge } from '@/components/ui/badge';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { useJobRoles, JobRoleWithStats } from '@/hooks/useJobRoles';
import { Plus, Edit, Search, Users, TrendingUp } from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import RoleFormModal from './RoleFormModal';

interface ManageRolesProps {
  onViewChange?: (view: string, filters?: {
    jobRoleId?: string;
    jobRoleTitle?: string;
  }) => void;
}

const ManageRoles = ({ onViewChange }: ManageRolesProps) => {
  const navigate = useNavigate();
  const { jobRoles, isLoading } = useJobRoles();
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [editingRole, setEditingRole] = useState<JobRoleWithStats | null>(null);
  const [searchTerm, setSearchTerm] = useState('');

  const filteredRoles = jobRoles?.filter(role => 
    role.title.toLowerCase().includes(searchTerm.toLowerCase()) ||
    role.department.toLowerCase().includes(searchTerm.toLowerCase()) ||
    role.location.toLowerCase().includes(searchTerm.toLowerCase())
  ) || [];

  const getPriorityColor = (priority: string) => {
    switch (priority) {
      case 'high':
        return 'destructive';
      case 'medium':
        return 'default';
      case 'low':
        return 'secondary';
      default:
        return 'secondary';
    }
  };

  const getPerformanceColor = (totalReferrals: number) => {
    if (totalReferrals >= 10) return 'text-green-600';
    if (totalReferrals >= 5) return 'text-gray-600';
    return 'text-gray-600';
  };

  const formatSalaryRange = (salaryMin?: string | number | null, salaryMax?: string | number | null) => {
    const min = typeof salaryMin === 'string' ? parseFloat(salaryMin) : salaryMin;
    const max = typeof salaryMax === 'string' ? parseFloat(salaryMax) : salaryMax;
    
    if (min && max) {
      return `${min.toLocaleString()} - ${max.toLocaleString()}`;
    } else if (min) {
      return `${min.toLocaleString()}+`;
    } else if (max) {
      return `Up to ${max.toLocaleString()}`;
    }
    return 'Not specified';
  };

  const startEdit = (role: JobRoleWithStats) => {
    setEditingRole(role);
  };

  const closeModal = () => {
    setShowCreateModal(false);
    setEditingRole(null);
  };

  const handleReferralCountClick = (role: JobRoleWithStats) => {
    if (role.total_referrals > 0) {
      navigate(`/referrals/${role.id}`);
    }
  };

  const handleRowClick = (role: JobRoleWithStats, event: React.MouseEvent) => {
    // Prevent navigation if clicking on interactive elements
    const target = event.target as HTMLElement;
    if (target.closest('button') || target.closest('a') || target.tagName === 'BUTTON') {
      return;
    }
    navigate(`/roles/${role.id}`);
  };

  if (isLoading) {
    return (
      <div className="p-6">
        <div className="text-center py-8">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600 mx-auto"></div>
          <p className="mt-2 text-gray-600">Loading roles...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="p-6 space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-3xl font-bold text-gray-900">Manage Roles</h1>
          <p className="text-gray-600 mt-1">Overview of all job roles and their referral performance</p>
        </div>
        <Button onClick={() => setShowCreateModal(true)}>
          <Plus className="w-4 h-4 mr-2" />
          Add New Role
        </Button>
      </div>

      {/* Search and Filters */}
      <Card>
        <CardContent className="p-4">
          <div className="flex items-center space-x-4">
            <div className="relative flex-1 max-w-md">
              <Search className="w-4 h-4 absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400" />
              <Input
                placeholder="Search roles by title, department, or location..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="pl-10"
              />
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Summary Stats */}
      <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
        <Card>
          <CardContent className="p-4">
            <div className="flex items-center space-x-2">
              <div className="p-2 bg-blue-100 rounded-lg">
                <Users className="w-4 h-4 text-blue-600" />
              </div>
              <div>
                <p className="text-sm text-gray-600">Total Roles</p>
                <p className="text-xl font-bold">{filteredRoles.length}</p>
              </div>
            </div>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="p-4">
            <div className="flex items-center space-x-2">
              <div className="p-2 bg-green-100 rounded-lg">
                <TrendingUp className="w-4 h-4 text-green-600" />
              </div>
              <div>
                <p className="text-sm text-gray-600">Active Roles</p>
                <p className="text-xl font-bold">{filteredRoles.filter(r => r.is_active).length}</p>
              </div>
            </div>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="p-4">
            <div className="flex items-center space-x-2">
              <div className="p-2 bg-purple-100 rounded-lg">
                <Users className="w-4 h-4 text-purple-600" />
              </div>
              <div>
                <p className="text-sm text-gray-600">Total Referrals</p>
                <p className="text-xl font-bold">{filteredRoles.reduce((sum, r) => sum + r.total_referrals, 0)}</p>
              </div>
            </div>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="p-4">
            <div className="flex items-center space-x-2">
              <div className="p-2 bg-orange-100 rounded-lg">
                <TrendingUp className="w-4 h-4 text-orange-600" />
              </div>
              <div>
                <p className="text-sm text-gray-600">Successful Hires</p>
                <p className="text-xl font-bold">{filteredRoles.reduce((sum, r) => sum + r.hired_count, 0)}</p>
              </div>
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Roles Table */}
      <Card>
        <CardHeader>
          <CardTitle>All Roles</CardTitle>
        </CardHeader>
        <CardContent>
          {filteredRoles.length === 0 ? (
            <div className="text-center py-12 text-gray-500">
              {searchTerm ? (
                'No roles match your search.'
              ) : (
                <div className="space-y-4">
                  <p>No job roles found. Create your first role to get started.</p>
                  <Button onClick={() => setShowCreateModal(true)}>
                    <Plus className="w-4 h-4 mr-2" />
                    Create your first role
                  </Button>
                </div>
              )}
            </div>
          ) : (
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Role</TableHead>
                  <TableHead>Department</TableHead>
                  <TableHead>Location</TableHead>
                  <TableHead>Salary Range</TableHead>
                  <TableHead>Referrals</TableHead>
                  <TableHead>Status</TableHead>
                  <TableHead></TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {filteredRoles.map((role) => (
                  <TableRow
                    key={role.id}
                    className="cursor-pointer hover:bg-gray-50"
                    onClick={(e) => handleRowClick(role, e)}
                  >
                    <TableCell>
                      <div>
                        <div className="font-medium">{role.title}</div>
                        <div className="flex items-center space-x-1 mt-1">
                          <Badge variant={getPriorityColor(role.priority || 'medium')} className="text-xs">
                            {role.priority} priority
                          </Badge>
                        </div>
                      </div>
                    </TableCell>
                    <TableCell>{role.department}</TableCell>
                    <TableCell>{role.location}</TableCell>
                    <TableCell>
                      <span className="text-sm">{formatSalaryRange(role.salary_min, role.salary_max)}</span>
                    </TableCell>
                    <TableCell>
                      <div className="space-y-1">
                        <button
                          onClick={(e) => {
                            e.stopPropagation();
                            handleReferralCountClick(role);
                          }}
                          className={`font-bold text-lg ${getPerformanceColor(role.total_referrals)} ${
                            role.total_referrals > 0 ? 'hover:underline cursor-pointer' : 'cursor-default'
                          }`}
                          disabled={role.total_referrals === 0}
                        >
                          {role.total_referrals}
                        </button>
                      </div>
                    </TableCell>
                    <TableCell>
                      <Badge variant={role.is_active ? "default" : "secondary"}>
                        {role.is_active ? 'Active' : 'Inactive'}
                      </Badge>
                    </TableCell>
                    <TableCell>
                      {/* Edit button can be added here if needed */}
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          )}
        </CardContent>
      </Card>

      {/* Role Form Modal */}
      <RoleFormModal 
        isOpen={showCreateModal || !!editingRole} 
        onClose={closeModal} 
        editingRole={editingRole} 
      />
    </div>
  );
};

export default ManageRoles;
